/**
 * 大屏飞机配置组件
 * 功能：管理大屏显示的飞机列表及其顺序（同时作用于飞行时间轴和机队卡片）
 */
import React, { useState } from "react";
import { Card, Table, Button, Space, Popconfirm, Tag, Empty, Form, Select, Switch, message } from "antd";
import { 
    PlusOutlined, 
    DeleteOutlined, 
    ArrowUpOutlined, 
    ArrowDownOutlined,
    CheckCircleOutlined,
    StopOutlined
} from "@ant-design/icons";
import AircraftTag from "../../../components/AircraftTag";

const { Option } = Select;

/**
 * 飞行时间轴飞机配置组件
 */
const TimelineAircraftConfig = ({ 
    dataSource = [],
    allAircraftList = [], // 所有可用飞机列表（用于下拉选择）
    loading = false,
    onAdd,
    onDelete,
    onToggleActive,
    onMoveUp,
    onMoveDown
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // 处理添加飞机
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            // 计算新飞机的显示顺序（放到最后）
            const maxOrder = dataSource.length > 0 
                ? Math.max(...dataSource.map(item => item.display_order)) 
                : 0;

            await onAdd({
                aircraft_registration: values.aircraft_registration,
                display_order: maxOrder + 1,
                is_active: true
            });

            form.resetFields();
        } catch (error) {
            if (!error.errorFields) {
                console.error("添加飞机失败:", error);
            }
        } finally {
            setSubmitting(false);
        }
    };

    // 过滤出未配置的飞机（用于下拉选择）
    const availableAircraft = allAircraftList.filter(
        aircraft => !dataSource.some(config => config.aircraft_registration === aircraft)
    );

    const columns = [
        {
            title: "顺序",
            dataIndex: "display_order",
            key: "display_order",
            width: 80,
            align: "center",
            render: (order) => <Tag color="blue">{order}</Tag>,
        },
        {
            title: "飞机注册号",
            dataIndex: "aircraft_registration",
            key: "aircraft_registration",
            width: 180,
            render: (registration) => (
                <AircraftTag registration={registration} showType={true} />
            ),
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (isActive, record) => (
                <Switch
                    checked={isActive}
                    checkedChildren={<CheckCircleOutlined />}
                    unCheckedChildren={<StopOutlined />}
                    onChange={(checked) => onToggleActive && onToggleActive(record, checked)}
                    disabled={loading}
                />
            ),
        },
        {
            title: "操作",
            key: "action",
            width: 200,
            fixed: "right",
            render: (_, record, index) => (
                <Space size="small">
                    {/* 上移 */}
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        onClick={() => onMoveUp && onMoveUp(record, index)}
                        disabled={index === 0 || loading}
                        title="上移"
                    />
                    
                    {/* 下移 */}
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        onClick={() => onMoveDown && onMoveDown(record, index)}
                        disabled={index === dataSource.length - 1 || loading}
                        title="下移"
                    />
                    
                    {/* 删除 */}
                    <Popconfirm
                        title="确认删除"
                        description={`确定要从大屏移除飞机 ${record.aircraft_registration} 吗？`}
                        onConfirm={() => onDelete && onDelete(record)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={loading}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title={`大屏飞机配置（${dataSource.length}架）`}
            size="small"
            extra={
                <Space>
                    <span style={{ color: "#999", fontSize: "12px" }}>
                        提示：可以调整显示顺序和激活状态
                    </span>
                </Space>
            }
        >
            {/* 添加飞机表单 */}
            <Form
                form={form}
                layout="inline"
                onFinish={handleSubmit}
                style={{ marginBottom: 16 }}
            >
                <Form.Item
                    name="aircraft_registration"
                    rules={[
                        { required: true, message: "请选择飞机" }
                    ]}
                    style={{ width: 200 }}
                >
                    <Select
                        placeholder="选择飞机"
                        showSearch
                        disabled={submitting || loading}
                        filterOption={(input, option) =>
                            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {availableAircraft.map(aircraft => (
                            <Option key={aircraft} value={aircraft}>
                                {aircraft}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<PlusOutlined />}
                        loading={submitting}
                        disabled={loading || availableAircraft.length === 0}
                    >
                        添加到大屏
                    </Button>
                </Form.Item>
            </Form>

            {/* 已配置飞机列表 */}
            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <p style={{ marginBottom: 8 }}>暂无配置数据</p>
                                    <p style={{ color: "#999", fontSize: 12 }}>
                                        请在上方选择要在大屏显示的飞机
                                    </p>
                                </div>
                            }
                        />
                    ),
                }}
            />

            {/* 说明文字 */}
            <div style={{ marginTop: 16, padding: "12px", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                    <strong>说明：</strong>
                    <br />
                    • 此列表同时控制大屏的飞行时间轴和下方机队卡片
                    <br />
                    • 只有激活状态的飞机才会显示在大屏上
                    <br />
                    • 显示顺序决定时间轴从上到下、机队卡片从左到右的排列
                    <br />
                    • 大屏每30秒自动刷新，配置变更最迟半分钟内生效
                </p>
            </div>
        </Card>
    );
};

export default TimelineAircraftConfig;
